/**
 * Отдельный vuex модуль для взаимодействия по pacs/analytics-API
 */
import makeMessage from "@/store/analytics/analyticMessage.js";
import {ANALYTICS} from "@/store/analytics/helpers.js";
import {cancelRequestOnReentry, handleErrorInRequest} from "camsng-frontend-shared/lib/requestAnnihilator.js";

// actions для analytics. Вызов действия начинать с "pacs/analytics/"
export const ACTION_LOAD_PACS_THERMAL_VISION_REPORT = "LOAD_PACS_THERMAL_VISION_REPORT";
export const ACTION_LOAD_PACS_MOTION_ALARM_REPORT = "LOAD_PACS_MOTION_ALARM_REPORT";
export const ACTION_LOAD_PACS_CAR_NUMBER_REPORT = "LOAD_PACS_CAR_NUMBER_REPORT";
export const ACTION_LOAD_PACS_HELMET_REPORT = "LOAD_PACS_HELMET_REPORT";
export const ACTION_LOAD_PACS_MASK_REPORT = "LOAD_PACS_MASK_REPORT";
export const ACTION_LOAD_PACS_PERIMETER_SECURITY_REPORT = "LOAD_PACS_PERIMETER_SECURITY_REPORT";
export const ACTION_LOAD_PACS_AIRCRAFT_REPORT = "LOAD_PACS_AIRCRAFT_REPORT";
export const ACTION_LOAD_PACS_FALL_DETECTION_REPORT = "LOAD_PACS_FALL_DETECTION_REPORT";
export const ACTION_LOAD_PACS_PARKING_DETECTION_REPORT = "LOAD_PACS_PARKING_DETECTION_REPORT";
export const ACTION_LOAD_PACS_CROWD_REPORT = "LOAD_PACS_CROWD_REPORT";
export const ACTION_LOAD_PACS_TRANSPORT_DETECTION_REPORT = "LOAD_PACS_TRANSPORT_DETECTION_REPORT";
export const ACTION_LOAD_PACS_CONTAINER_MONITORING_REPORT = "LOAD_PACS_CONTAINER_MONITORING_REPORT";

export default {
  namespaced: true,
  actions: {
    /**
     * Загрузка перечня событий по тепловизору с камеры для формирования отчета.
     *
     * @param {Object} context
     * @param {Date} archiveFrom
     * @param {Date} archiveTo
     * @param {Number} minTemperature
     * @param {Number} maxTemperature
     * @param {Boolean} alarm
     * @param {String} query
     * @return {Promise.<Array.<ThermalVisionMessage>>}
     */
    async [ACTION_LOAD_PACS_THERMAL_VISION_REPORT](context, {
      archiveFrom,
      archiveTo,
      minTemperature,
      maxTemperature,
      alarm = null,
      query = null,
      page = 1,
      pageSize
    }) {
      try {
        const response = await this.getters.ajaxClient.post("/v0/pacs/analytics/thermal_vision/report/", {
          start: archiveFrom.toISOString(),
          end: archiveTo.toISOString(),
          temperature_min: minTemperature,
          temperature_max: maxTemperature,
          alarm: alarm,
          query: query,
          page_size: pageSize,
          page: page,
        }, {cancelToken: cancelRequestOnReentry(ACTION_LOAD_PACS_THERMAL_VISION_REPORT)});
        return {
          results: response.data.results.map(rawEventData => makeMessage(rawEventData, null, ANALYTICS.thermal_vision)),
          count: response.data.count, // Общее количество записей
          page: response.data.page, // Информация о страницах
        };
      } catch (error) {
        handleErrorInRequest(error);
      }
    },
    /**
     * Загрузка перечня событий по распознаванию движения с камеры для формирования отчета.
     *
     * @param {Object} context
     * @param {Date} archiveFrom
     * @param {Date} archiveTo
     * @return {Promise.<Array.<MotionAlarmMessage>>}
     */
    async [ACTION_LOAD_PACS_MOTION_ALARM_REPORT](context, {archiveFrom, archiveTo, page = 1,pageSize}) {
      try {
        const response = await this.getters.ajaxClient.post("/v0/pacs/analytics/motion_alarm/report/", {
          start: archiveFrom.toISOString(),
          end: archiveTo.toISOString(),
          page_size: pageSize,
          page: page,
        }, {cancelToken: cancelRequestOnReentry(ACTION_LOAD_PACS_MOTION_ALARM_REPORT)});
        return {
          results: response.data.results.map(rawEventData => makeMessage(rawEventData, null, ANALYTICS.motion_alarm)),
          count: response.data.count, // Общее количество записей
          page: response.data.page, // Информация о страницах
        };
      } catch (error) {
        handleErrorInRequest(error);
      }
    },
    /**
     * Загрузка перечня событий по распознаванию касок для формирования отчета.
     *
     * @param {Object} context
     * @param {Date} archiveFrom
     * @param {Date} archiveTo
     * @return {Promise.<Array.<HelmetMessage>>}
     */
    async [ACTION_LOAD_PACS_HELMET_REPORT](context, {archiveFrom, archiveTo, page = 1,pageSize}) {
      try {
        const response = await this.getters.ajaxClient.post("/v0/gangs/analytics/helmet/report/", {
          start: archiveFrom.toISOString(),
          end: archiveTo.toISOString(),
          page_size: pageSize,
          page: page,
        }, {cancelToken: cancelRequestOnReentry(ACTION_LOAD_PACS_HELMET_REPORT)});
        return {
          results: response.data.results.map(rawEventData => makeMessage(rawEventData, null, ANALYTICS.helmet)),
          count: response.data.count, // Общее количество записей
          page: response.data.page, // Информация о страницах
        };
      } catch (error) {
        handleErrorInRequest(error);
      }
    },
    /**
     * Загрузка перечня событий по вторжению в зону для формирования отчета.
     *
     * @param {Object} context
     * @param {Date} archiveFrom
     * @param {Date} archiveTo
     * @return {Promise.<Array.<MaskMessage>>}
     */
    async [ACTION_LOAD_PACS_PERIMETER_SECURITY_REPORT](context, {archiveFrom, archiveTo, page, pageSize}) {
      try {
        const response = await this.getters.ajaxClient.post("/v0/gangs/analytics/perimeter_security/report/", {
          start: archiveFrom.toISOString(),
          end: archiveTo.toISOString(),
          page_size: pageSize,
          page: page,
        }, {cancelToken: cancelRequestOnReentry(ACTION_LOAD_PACS_PERIMETER_SECURITY_REPORT)});
        return {
          results: response.data.results.map(rawEventData => makeMessage(rawEventData, null, ANALYTICS.perimeter_security)),
          count: response.data.count, // Общее количество записей
          page: response.data.page, // Информация о страницах
        };
      } catch (error) {
        handleErrorInRequest(error);
      }
    },
    /**
     * Загрузка перечня событий по ЛА для формирования отчета.
     *
     * @param {Object} context
     * @param {Date} archiveFrom
     * @param {Date} archiveTo
     * @return {Promise.<Array.<AircraftMessage>>}
     */
    async [ACTION_LOAD_PACS_AIRCRAFT_REPORT](context, {archiveFrom, archiveTo, page, pageSize}) {
      try {
        const response = await this.getters.ajaxClient.post("/v0/gangs/analytics/aircraft/report/", {
          start: archiveFrom.toISOString(),
          end: archiveTo.toISOString(),
          page_size: pageSize,
          page: page,
        }, {cancelToken: cancelRequestOnReentry(ACTION_LOAD_PACS_AIRCRAFT_REPORT)});
        return {
          results: response.data.results.map(rawEventData => makeMessage(rawEventData, null, ANALYTICS.aircraft)),
          count: response.data.count, // Общее количество записей
          page: response.data.page, // Информация о страницах
        };
      } catch (error) {
        handleErrorInRequest(error);
      }
    },
    /**
     * Загрузка перечня событий по доступу к мусоркам для формирования отчета.
     *
     * @param {Object} context
     * @param {Date} archiveFrom
     * @param {Date} archiveTo
     * @return {Promise.<Array.<TransportDetectionMessage>>}
     */
    async [ACTION_LOAD_PACS_TRANSPORT_DETECTION_REPORT](context, {archiveFrom, archiveTo, page, pageSize}) {
      try {
        const response = await this.getters.ajaxClient.post("/v0/analytics/transport_detection/report/", {
          start: archiveFrom.toISOString(),
          end: archiveTo.toISOString(),
          page_size: pageSize,
          page: page,
        }, {cancelToken: cancelRequestOnReentry(ACTION_LOAD_PACS_TRANSPORT_DETECTION_REPORT)});
        return {
          results: response.data.results.map(rawEventData => makeMessage(rawEventData, null, ANALYTICS.transport_detection)),
          count: response.data.count, // Общее количество записей
          page: response.data.page, // Информация о страницах
        };
      } catch (error) {
        handleErrorInRequest(error);
      }
    },
    /**
     * Загрузка перечня событий по контролю ТКО для формирования отчета.
     *
     * @param {Object} context
     * @param {Date} archiveFrom
     * @param {Date} archiveTo
     * @return {Promise.<Array.<ContainerMonitoringMessage>>}
     */
    async [ACTION_LOAD_PACS_CONTAINER_MONITORING_REPORT](context, {archiveFrom, archiveTo, page, pageSize}) {
      try {
        const response = await this.getters.ajaxClient.post("/v0/analytics/container_monitoring/report/", {
          start: archiveFrom.toISOString(),
          end: archiveTo.toISOString(),
          page_size: pageSize,
          page: page,
        }, {cancelToken: cancelRequestOnReentry(ACTION_LOAD_PACS_CONTAINER_MONITORING_REPORT)});
        return {
          results: response.data.results.map(rawEventData => makeMessage(rawEventData, null, ANALYTICS.container_monitoring)),
          count: response.data.count, // Общее количество записей
          page: response.data.page, // Информация о страницах
        };
      } catch (error) {
        handleErrorInRequest(error);
      }
    },
    /**
     * Загрузка перечня событий по обнаружению падения для формирования отчета.
     *
     * @param {Object} context
     * @param {Date} archiveFrom
     * @param {Date} archiveTo
     * @return {Promise.<Array.<MaskMessage>>}
     */
    async [ACTION_LOAD_PACS_FALL_DETECTION_REPORT](context, {archiveFrom, archiveTo, page, pageSize}) {
      try {
        const response = await this.getters.ajaxClient.post("/v0/gangs/analytics/fall_detection/report/", {
          start: archiveFrom.toISOString(),
          end: archiveTo.toISOString(),
          page_size: pageSize,
          page: page,
        }, {cancelToken: cancelRequestOnReentry(ACTION_LOAD_PACS_FALL_DETECTION_REPORT)});
        return {
          results: response.data.results.map(rawEventData => makeMessage(rawEventData, null, ANALYTICS.fall_detection)),
          count: response.data.count, // Общее количество записей
          page: response.data.page, // Информация о страницах
        };
      } catch (error) {
        handleErrorInRequest(error);
      }
    },
    /**
     * Загрузка перечня событий по парковкам для формирования отчета.
     *
     * @param {Object} context
     * @param {Date} archiveFrom
     * @param {Date} archiveTo
     * @return {Promise.<Array.<MaskMessage>>}
     */
    async [ACTION_LOAD_PACS_PARKING_DETECTION_REPORT](context, {archiveFrom, archiveTo,page, pageSize}) {
      try {
        const response = await this.getters.ajaxClient.post("/v0/gangs/analytics/parking_detection/report/", {
          start: archiveFrom.toISOString(),
          end: archiveTo.toISOString(),
          page_size: pageSize,
          page: page,
        }, {cancelToken: cancelRequestOnReentry(ACTION_LOAD_PACS_PARKING_DETECTION_REPORT)});
        return {
          results: response.data.results.map(rawEventData => makeMessage(rawEventData, null, ANALYTICS.parking_detection)),
          count: response.data.count, // Общее количество записей
          page: response.data.page, // Информация о страницах
        };
      } catch (error) {
        handleErrorInRequest(error);
      }
    },
    /**
     * Загрузка перечня событий по распознаванию масок для формирования отчета.
     *
     * @param {Object} context
     * @param {Date} archiveFrom
     * @param {Date} archiveTo
     * @return {Promise.<Array.<MaskMessage>>}
     */
    async [ACTION_LOAD_PACS_MASK_REPORT](context, {archiveFrom, archiveTo,page, pageSize}) {
      try {
        const response = await this.getters.ajaxClient.post("/v0/gangs/analytics/mask/report/", {
          start: archiveFrom.toISOString(),
          end: archiveTo.toISOString(),
          page_size: pageSize,
          page: page,
        }, {cancelToken: cancelRequestOnReentry(ACTION_LOAD_PACS_MASK_REPORT)});
        return {
          results: response.data.results.map(rawEventData => makeMessage(rawEventData, null, ANALYTICS.mask)),
          count: response.data.count, // Общее количество записей
          page: response.data.page, // Информация о страницах
        };
      } catch (error) {
        handleErrorInRequest(error);
      }
    },
    /**
     * Загрузка перечня событий по распознаванию скопления людей для формирования отчета.
     *
     * @param {Object} context
     * @param {Date} archiveFrom
     * @param {Date} archiveTo
     * @return {Promise.<Array.<MaskMessage>>}
     */
    async [ACTION_LOAD_PACS_CROWD_REPORT](context, {archiveFrom, archiveTo,page, pageSize}) {
      try {
        const response = await this.getters.ajaxClient.post("/v0/gangs/analytics/crowd/report/", {
          start: archiveFrom.toISOString(),
          end: archiveTo.toISOString(),
          page_size: pageSize,
          page: page,
        }, {cancelToken: cancelRequestOnReentry(ACTION_LOAD_PACS_CROWD_REPORT)});
        return {
          results: response.data.results.map(rawEventData => makeMessage(rawEventData, null, ANALYTICS.crowd)),
          count: response.data.count, // Общее количество записей
          page: response.data.page, // Информация о страницах
        };
      } catch (error) {
        handleErrorInRequest(error);
      }
    },
    /**
     * Загрузка перечня событий по автомобильным номерам с камеры для формирования отчета.
     *
     * @param {Object} context
     * @param {Date} archiveFrom
     * @param {Date} archiveTo
     * @param {String} number
     * @param {Boolean} access
     * @param {String} query
     * @param {Number} page
     * @param {Number} pageSize
     * @return {Promise.<Array.<CarNumberMessage>>}
     */
    async [ACTION_LOAD_PACS_CAR_NUMBER_REPORT](context, {
      archiveFrom,
      archiveTo,
      number = null,
      access = null,
      query = null,
      page = 1,
      pageSize,
    }) {
      try {
        const requestData = {
          start: archiveFrom.toISOString(),
          end: archiveTo.toISOString(),
          number: number,
          access: access,
          query: query,
          page_size: pageSize,
          page: page,
        };

        // Условно добавляем параметр page, если поля number и query пустые
        // Отправляем запрос
        const response = await this.getters.ajaxClient.post("/v0/pacs/analytics/car_number/report/", requestData, {
          cancelToken: cancelRequestOnReentry(ACTION_LOAD_PACS_CAR_NUMBER_REPORT),
        });

        return {
          results: response.data.results.map(rawEventData => makeMessage(rawEventData, null, ANALYTICS.car_number)),
          count: response.data.count, // Общее количество записей
          page: response.data.page, // Информация о страницах
        };
      } catch (error) {
        handleErrorInRequest(error);
      }
    }

  }
};
